import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { GiftCard } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { customerQueryOptions } from '@/features/customers';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { toGiftCardSend } from '../api/mappers';
import { useSendGiftCard } from '../hooks/use-gift-card-mutations';
import {
  sendGiftCardSchema,
  type SendGiftCardInput,
  type SendGiftCardOutput,
} from '../schemas/gift-card-form-schema';
import { sendTargets, type SendTarget } from '../schemas/gift-card-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['to', 'message']);

export interface SendGiftCardDialogProps {
  storeId: string;
  card: GiftCard;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function SendForm({
  storeId,
  card,
  targets,
  onDone,
}: {
  storeId: string;
  card: GiftCard;
  targets: SendTarget[];
  onDone: () => void;
}) {
  const { t } = useTranslation(['giftCards', 'common']);
  const send = useSendGiftCard(storeId);
  const message = useErrorMessage();
  const form = useForm<SendGiftCardInput, unknown, SendGiftCardOutput>({
    resolver: useI18nResolver(sendGiftCardSchema),
    defaultValues: { to: targets[0]?.value ?? '', message: '' },
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      const sent = await send.mutateAsync({ id: card.id, body: toGiftCardSend(values) });
      toast.success(t('giftCards:toasts.sent', { to: sent.to }));
      onDone();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="to"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('giftCards:send.to')}</FormLabel>
              <FormControl>
                <RadioGroup value={field.value} onValueChange={field.onChange} className="gap-2">
                  {targets.map((target) => (
                    <label
                      key={target.kind}
                      htmlFor={`send-to-${target.kind}`}
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                        field.value === target.value && 'border-primary bg-primary-tint/40',
                      )}
                    >
                      <RadioGroupItem
                        id={`send-to-${target.kind}`}
                        value={target.value}
                        className="mt-0.5"
                      />
                      <span className="grid gap-0.5 text-sm">
                        <span className="font-medium">
                          {t(`giftCards:send.targets.${target.kind}`)}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {target.email ?? t('giftCards:send.customerEmailHidden')}
                        </span>
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormDescription>{t('giftCards:send.toHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('giftCards:fields.message')}</FormLabel>
              <FormControl>
                <Textarea rows={3} {...field} />
              </FormControl>
              <FormDescription>{t('giftCards:fields.messageHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onDone}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={send.isPending}>
            {send.isPending ? t('giftCards:send.submitting') : t('giftCards:send.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/**
 * Emails the code again. The backend accepts only the attached customer and the recipient noted
 * at issue, so those are the choices; nothing can be typed in.
 */
export function SendGiftCardDialog({ storeId, card, open, onOpenChange }: SendGiftCardDialogProps) {
  const { t } = useTranslation('giftCards');
  const { can } = usePermissions();
  const customer = useQuery({
    ...customerQueryOptions(storeId, card.customerId ?? ''),
    enabled: open && card.customerId !== null && can('customers.read'),
  });
  const waiting = customer.isLoading;
  const targets = sendTargets(card, customer.data?.email ?? null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('send.title')}</DialogTitle>
          <DialogDescription>{t('send.description', { last4: card.last4 })}</DialogDescription>
        </DialogHeader>
        {waiting ? (
          <Skeleton className="h-24" />
        ) : (
          <SendForm
            key={targets.map((target) => target.value).join('|')}
            storeId={storeId}
            card={card}
            targets={targets}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
